import {
  fetchDispatchReservations,
  fetchDispatchVehicles,
  fetchDispatchVehicleTypes,
  fetchStatuses,
  saveDispatchReservation,
} from '../api/dispatchService';
import { fetchBookingCalc, toCalcDateTime, type BookingCalcVehicle, type BookingCalcResponse } from '../api/bookingCalcService';
import { hasStatusCache, setStatusCache } from '../lib/statusMapper';

function getCurrentStaffName(): string {
  try {
    const raw = localStorage.getItem('bo_staff');
    if (!raw) return '';
    const parsed = JSON.parse(raw);
    return typeof parsed?.name === 'string' ? parsed.name.trim() : '';
  } catch {
    return '';
  }
}

/**
 * 予約保存前に status キャッシュ（statusMapper）が populate されていることを保証する。
 *
 * 通常は App.tsx 起動時に useReservationStatuses → setStatusCache が走るが、
 * モバイルで起動直後にエージェントから予約作成するとキャッシュが空のまま走り、
 * resolveStatusId('booked') が null を返して "status field is required" になる事故が
 * 発生していた。保険として保存直前に cache を見てなければ自前で fetch する。
 */
async function ensureStatusCachePopulated(): Promise<void> {
  if (hasStatusCache()) return;
  try {
    const data = await fetchStatuses();
    if (data?.data) {
      setStatusCache({
        reservation: data.data.reservation || {},
        vehicle: data.data.vehicle || {},
        vehicle_location: data.data.vehicle_location || {},
        vehicle_type: data.data.vehicle_type || {},
      });
    }
  } catch {
    // キャッシュ populate に失敗してもフロー自体は続行（save 側で再エラーになる）
  }
}

/**
 * 予約データの memo に追記するタイムラインエントリを構築する。
 * 既存の `useAutoChangeMemo.formatAutoMemoEntry` と同じフォーマット
 * （`---アクション スタッフ名 YYYY-MM-DD HH:MM---` ヘッダー + 内容 + `---` フッター）に揃える。
 * スタッフ名に `(エージェント)` を付けて手動操作と区別できるようにする。
 */
type AgentMemoAction = 'reservation' | 'cancel';
const AGENT_MEMO_ACTION_LABEL: Record<AgentMemoAction, string> = {
  reservation: '予約処理',
  cancel: 'キャンセル処理',
};

function formatTimelineTimestamp(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const mi = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d} ${hh}:${mi}`;
}

function buildAgentMemoEntry(action: AgentMemoAction, contentLines: string[]): string {
  const ts = formatTimelineTimestamp();
  const staffName = getCurrentStaffName();
  const label = staffName ? `${staffName}(エージェント)` : 'エージェント';
  const header = `---${AGENT_MEMO_ACTION_LABEL[action]} ${label} ${ts}---`;
  const body = contentLines.filter(Boolean).join('\n');
  return body ? `${header}\n${body}\n---` : `${header}\n---`;
}

function appendAgentMemoEntry(existingMemo: unknown, entry: string): string {
  const cur = String(existingMemo || '').trimEnd();
  return cur ? `${cur}\n${entry}` : entry;
}
import type { PendingConfirm, WizardField } from './types';
import { RESERVATION_WIZARD_FIELDS } from './wizard';

export const AGENT_TOOL_DECLARATIONS = [
  {
    name: 'search_reservations',
    description: [
      '予約を検索する。**予約番号 (contact_code)**・日付範囲・顧客名・電話番号で絞り込める。少なくとも1つの引数を指定すること。',
      'ユーザーが「00001NYYHSの予約」「H26-0413-55」のような英数字コードを言及した場合は、',
      'それを contact_code として検索すること。contact_code 指定時は日付範囲を渡さない（全期間から1件特定する）。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        contact_code: {
          type: 'string',
          description: '予約番号（例: "00001NYYHS", "H26-0413-55", "TR-ABC123"）。完全一致でバックエンド検索する。',
        },
        start_date: {
          type: 'string',
          description: '貸出日範囲の開始日 (YYYY-MM-DD)。省略すると今日から過去30日。contact_code 指定時は不要。',
        },
        end_date: {
          type: 'string',
          description: '貸出日範囲の終了日 (YYYY-MM-DD)。省略すると今日から未来90日。contact_code 指定時は不要。',
        },
        customer_name: {
          type: 'string',
          description: '予約者名（部分一致）',
        },
        phone: {
          type: 'string',
          description: '電話番号（部分一致、ハイフン無視）',
        },
      },
    },
  },
  {
    name: 'search_vehicle_types',
    description: '車両タイプを検索する。車両タイプ名/コード/カテゴリに加えて、具体的な車両コード・車両番号（例: M-D5-H）でも検索でき、該当する車両が属する車両タイプを返す。pickup_date/return_date を指定すると、該当期間の在庫（空き車両数）も含めて返す。見つからない場合は全車両タイプの一覧を alternatives として返す。',
    parameters: {
      type: 'object',
      properties: {
        query: {
          type: 'string',
          description: '車両タイプ名/コード、または具体的な車両コード・車両番号の一部（例: "アルファード", "M-D5-H", "デリカ"）',
        },
        pickup_date: {
          type: 'string',
          description: '在庫を確認したい貸出日 (YYYY-MM-DD)。省略時は在庫情報を含めない。',
        },
        return_date: {
          type: 'string',
          description: '在庫を確認したい返却日 (YYYY-MM-DD)。pickup_date とセットで指定する。',
        },
      },
      required: ['query'],
    },
  },
  {
    name: 'list_all_vehicle_types',
    description: [
      '全車両タイプの料金一覧を期間付きで取得する。',
      '「6/3 - 6/5の予約」のように**日付のみで車両タイプが未指定**の曖昧な問い合わせを受けた際、',
      '全候補車両タイプと各々の合計料金、在庫を一括で返す。',
      '時刻が省略されている場合は 12:00 を仮定する。スタッフが電話応対中に即答するための一括取得ツール。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        pickup_date: { type: 'string', description: '貸出日 (YYYY-MM-DD)' },
        return_date: { type: 'string', description: '返却日 (YYYY-MM-DD)' },
      },
      required: ['pickup_date', 'return_date'],
    },
  },
  {
    name: 'calculate_price',
    description: [
      '指定車両タイプ・期間の料金を計算する。**お客様の見積もり問い合わせにも必ずこのツールを使う**。',
      'NOC免責 / CDW / チャイルドシート / ブースターシート / 複数台数まで一括で計算可能。',
      'pickup_datetime / return_datetime（YYYY-MM-DD HH:mm）を渡すと正確な時刻で計算する。',
      'pickup_date / return_date（YYYY-MM-DD）でも動くがその場合は 12:00 を仮定する。',
      '時刻が「14時頃」「13時半」のような曖昧表現でも **ユーザーに確認せず確定値**（14:00, 13:30）として渡してよい。',
      'レスポンスは per_vehicle（1 台分の内訳）と grand_total（quantity 台分の合計）を含む。',
      '「15%割引だといくら」のような **仮定計算** を求められた場合は per_vehicle.base_subtotal に (1 - X%) を掛けて算出すること（割引後の値に重ねがけしない）。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        vehicle_type_id: { type: 'integer', description: '車両タイプ ID' },
        pickup_datetime: { type: 'string', description: '貸出日時 (YYYY-MM-DD HH:mm) — 推奨' },
        return_datetime: { type: 'string', description: '返却日時 (YYYY-MM-DD HH:mm) — 推奨' },
        pickup_date: { type: 'string', description: '貸出日 (YYYY-MM-DD)。datetime 未指定時に 12:00 を仮定' },
        return_date: { type: 'string', description: '返却日 (YYYY-MM-DD)' },
        noc_waiver: { type: 'boolean', description: 'NOC 免責の有無（料金に加算）' },
        cdw: { type: 'boolean', description: 'CDW の有無（料金に加算）' },
        child_seat: { type: 'integer', description: 'チャイルドシート台数' },
        booster_seat: { type: 'integer', description: 'ブースターシート台数' },
        quantity: { type: 'integer', description: '車両台数（既定 1）。複数台数の合計を計算する' },
      },
      required: ['vehicle_type_id'],
    },
  },
  {
    name: 'search_customer',
    description: '過去の予約から顧客を検索する（顧客マスタは無いため予約履歴から抽出）。',
    parameters: {
      type: 'object',
      properties: {
        name: { type: 'string', description: '顧客名（部分一致）' },
        phone: { type: 'string', description: '電話番号（部分一致）' },
      },
    },
  },
  {
    name: 'update_reservation',
    description: [
      '既存の予約を更新する。ステータス変更（キャンセルなど）・貸出日時/返却日時の変更・到着便/出発便の変更・乗車人数・コメントなどに対応。',
      '予約は **reservation_id（整数）または contact_code（予約番号）のどちらか1つ** で特定する。',
      '渡された項目だけが上書きされ、それ以外の既存値は維持される。',
      '呼び出すとフロントが (1) 既存予約を取得 → (2) 差分確認カードをユーザーに提示 → (3) 承認後にバックエンドへ保存、を自動で行う。',
      'ユーザーに「確認しますか？」と聞かずにすぐ呼び出してよい（確認カードはフロントが出す）。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        reservation_id: { type: 'integer', description: '予約 ID（search_reservations の results[i].id）' },
        contact_code: { type: 'string', description: '予約番号（例: "00001NYYHS"）。reservation_id が無いときに使う。' },
        status: {
          type: 'string',
          description: 'ステータスコード。利用可能: booked(予約確定) / cancelled(キャンセル) / done(貸出済み) / in_use(貸出中) / pending(確認中) / received(受信済み) / mail_ready(要連絡) / offer(オファー中) / nonreserved(予約に至らなかった)',
        },
        pickup_datetime: { type: 'string', description: '新しい貸出日時 (YYYY-MM-DD HH:mm)' },
        return_datetime: { type: 'string', description: '新しい返却日時 (YYYY-MM-DD HH:mm)' },
        arrival_flight: { type: 'string', description: '到着便名（例: NH123）。空文字でクリア。' },
        departure_flight: { type: 'string', description: '出発便名（例: JL987）。空文字でクリア。' },
        customer_name: { type: 'string', description: '予約者名' },
        phone: { type: 'string', description: '電話番号' },
        email: { type: 'string', description: 'メールアドレス' },
        pax: { type: 'integer', description: '乗車人数（大人）' },
        pax_children: { type: 'integer', description: '子供の人数' },
        child_seat: { type: 'integer', description: 'チャイルドシート数' },
        booster_seat: { type: 'integer', description: 'ブースターシート数' },
        noc_waiver: { type: 'boolean', description: 'NOC 免責の有無' },
        cdw: { type: 'boolean', description: 'CDW の有無' },
        comment: { type: 'string', description: 'お客様の要望・コメント' },
        memo: { type: 'string', description: '社内メモ' },
      },
    },
  },
  {
    name: 'create_reservation',
    description: [
      '予約を新規作成する。',
      '【重要】必須4項目（customer_name, pickup_datetime, return_datetime, vehicle_type_id）が揃い、',
      'calculate_price で料金が確定したら、**ユーザーに「確認しますか？」と聞かずに直ちにこのツールを呼ぶこと**。',
      '呼び出すとフロントが自動的に:',
      '(1) 不足している重要質問項目（NOC/CDW/電話/メール/シート/コメント）をウィザードで一問一答で収集し、',
      '(2) 確認カードをユーザーに提示し、',
      '(3) 承認後にバックエンドに予約を作成する。',
      'よって重要質問項目はエージェントが自分で聞かないこと（ユーザーが自発的に伝えた値のみ引数に含める）。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        pickup_datetime: { type: 'string', description: '貸出日時 (YYYY-MM-DD HH:mm)（必須）' },
        return_datetime: { type: 'string', description: '返却日時 (YYYY-MM-DD HH:mm)（必須）' },
        vehicle_type_id: { type: 'integer', description: '車両タイプ ID（必須、search_vehicle_types で解決）' },
        customer_name: { type: 'string', description: '予約者名。Gemini は通常は渡さなくてよい（フロントウィザードが収集する）。ユーザーが既に名乗っていればここで渡してもよい。' },
        base_price: { type: 'integer', description: '基本料金（円）。省略時は calculate_price の結果を使用。' },
        noc_waiver: { type: 'boolean', description: 'NOC 免責の有無' },
        cdw: { type: 'boolean', description: 'CDW の有無' },
        phone: { type: 'string', description: '電話番号' },
        email: { type: 'string', description: 'メールアドレス' },
        pax: { type: 'integer', description: '乗車人数（大人）' },
        pax_children: { type: 'integer', description: '子供の人数' },
        child_seat: { type: 'integer', description: 'チャイルドシート数' },
        booster_seat: { type: 'integer', description: 'ブースターシート数' },
        arrival_flight: { type: 'string', description: '到着便名（例: NH123）' },
        departure_flight: { type: 'string', description: '出発便名（例: JL987）' },
        comment: { type: 'string', description: 'お客様の要望・コメント' },
      },
      required: ['pickup_datetime', 'return_datetime', 'vehicle_type_id'],
    },
  },
];

const normalizePhone = (s: string): string => s.replace(/[-\s]/g, '').toLowerCase();

// メールとして有効な文字列のみ受け入れ、そうでなければ null（空欄）にする
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const sanitizeEmail = (value: unknown): string | null => {
  if (value == null) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  return EMAIL_REGEX.test(trimmed) ? trimmed : null;
};

// 電話番号: 数字とハイフン以外を含むもの（「不明」などの自由記述）は null にする
const PHONE_REGEX = /^[0-9+\-()\s]+$/;
const sanitizePhone = (value: unknown): string | null => {
  if (value == null) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  return PHONE_REGEX.test(trimmed) ? trimmed : null;
};

const toYmd = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const addDays = (d: Date, days: number): Date => {
  const next = new Date(d);
  next.setDate(next.getDate() + days);
  return next;
};

const ensureDateTimeIso = (input: string): string => {
  // 受け入れ: "2026-07-10 10:00" or "2026-07-10T10:00" or "2026-07-10T10:00:00+09:00"
  const trimmed = String(input).trim();
  if (trimmed.includes('+09:00')) return trimmed;
  const normalized = trimmed.replace(' ', 'T');
  if (/T\d{2}:\d{2}$/.test(normalized)) return `${normalized}:00+09:00`;
  if (/T\d{2}:\d{2}:\d{2}$/.test(normalized)) return `${normalized}+09:00`;
  // 日付のみ → 09:00 を仮置きはしない。エラーにする
  throw new Error(`日時の形式が不正です: ${input}（YYYY-MM-DD HH:mm 形式で指定してください）`);
};

// -------- 個別 tool 実装 --------

export async function runSearchReservations(args: Record<string, any>): Promise<any> {
  const contactCode = typeof args.contact_code === 'string' ? args.contact_code.trim() : '';

  // contact_code 指定時は日付範囲なしで全期間検索（一意キーなので 1 件返るのが普通）
  const fetchParams: Record<string, any> = {};
  let usedStartDate: string | null = null;
  let usedEndDate: string | null = null;
  if (contactCode) {
    fetchParams.contactCode = contactCode;
  } else {
    const today = new Date();
    usedStartDate = args.start_date || toYmd(addDays(today, -30));
    usedEndDate = args.end_date || toYmd(addDays(today, 90));
    fetchParams.startDate = usedStartDate;
    fetchParams.endDate = usedEndDate;
  }

  const reservations = await fetchDispatchReservations(fetchParams);
  const name = args.customer_name ? String(args.customer_name).toLowerCase() : null;
  const phone = args.phone ? normalizePhone(String(args.phone)) : null;

  const filtered = reservations.filter((r: any) => {
    if (name) {
      const cand = [r.customer_name, r.customerName, r.cname, r.contact_name]
        .filter(Boolean)
        .map((v: any) => String(v).toLowerCase())
        .join(' | ');
      if (!cand.includes(name)) return false;
    }
    if (phone) {
      const cand = [r.customer_tel, r.tel, r.customerTel, r.phone]
        .filter(Boolean)
        .map((v: any) => normalizePhone(String(v)))
        .join(' | ');
      if (!cand.includes(phone)) return false;
    }
    return true;
  });

  const simplified = filtered.slice(0, 30).map((r: any) => ({
    id: r.id ?? r.reservation_id,
    contact_code: r.contact_code ?? null,
    start_date: r.start_date,
    end_date: r.end_date,
    customer_name: r.customer_name ?? r.cname,
    customer_tel: r.customer_tel ?? r.tel,
    car_type: r.car_type,
    vehicle_id: r.vehicle_id ?? r.resource_id,
    status: r.status_code ?? r.status,
  }));

  return {
    count: filtered.length,
    truncated: filtered.length > 30,
    query: {
      contact_code: contactCode || null,
      start_date: usedStartDate,
      end_date: usedEndDate,
      customer_name: args.customer_name || null,
      phone: args.phone || null,
    },
    results: simplified,
  };
}

/**
 * 車両タイプ id ⇔ code を相互変換するためのマップを取得。
 * bookingCalc API は code (例: T-AV-H) しか返さないが、保存 API・create_reservation の
 * vehicle_id 選定では vehicle_type_id (整数) が必要なため。
 */
async function getVehicleTypeCodeMaps(): Promise<{
  codeToId: Map<string, number>;
  idToCode: Map<number, string>;
  byId: Map<number, any>;
}> {
  const types = await fetchDispatchVehicleTypes();
  const codeToId = new Map<string, number>();
  const idToCode = new Map<number, string>();
  const byId = new Map<number, any>();
  (types as any[]).forEach((t) => {
    const id = Number(t?.id);
    const code = String(
      t?.code ?? t?.vehicle_type_code ?? '',
    ).trim();
    if (Number.isFinite(id) && id > 0) {
      byId.set(id, t);
      if (code) {
        codeToId.set(code, id);
        idToCode.set(id, code);
      }
    }
  });
  return { codeToId, idToCode, byId };
}

const DEFAULT_TIME = '12:00';

/** YYYY-MM-DD → YYYY-MM-DDT12:00。HH:mm を含んでいればそのまま toCalcDateTime で正規化 */
function toCalcDateTimeFromAny(input: string, defaultTime = DEFAULT_TIME): string {
  const trimmed = String(input || '').trim();
  if (!trimmed) throw new Error('日時が空です');
  // HH:mm を含むかどうかをラフ判定
  const hasTime = /T\d{2}:\d{2}|\s\d{2}:\d{2}/.test(trimmed);
  if (hasTime) return toCalcDateTime(trimmed);
  return `${trimmed.slice(0, 10)}T${defaultTime}`;
}

/**
 * 指定車両タイプのうち、指定期間に予約が重ならない車両を 1 台選んで返す。
 * 空きがなければ null。
 *
 * 保存 API には `vehicle_type_id` フィールドが無く、`vehicle_id` を指定しないと
 * バックエンドが任意の車両を自動割当してしまうため、**フロント側で具体車両を 1 台選ぶ**必要がある。
 */
async function pickAvailableVehicleForType(
  vehicleTypeId: number,
  pickupDate: string,
  returnDate: string,
): Promise<{ id: number; vehicle_code: string; vehicle_number: string } | null> {
  const [vehicles, reservations] = await Promise.all([
    fetchDispatchVehicles({}),
    fetchDispatchReservations({ startDate: pickupDate, endDate: returnDate }),
  ]);

  const periodStart = new Date(`${pickupDate}T00:00:00`).getTime();
  const periodEnd = new Date(`${returnDate}T23:59:59`).getTime();

  const reservedVehicleIds = new Set<number | string>();
  reservations.forEach((r: any) => {
    const vehicleId = r.vehicle_id ?? r.resource_id;
    if (vehicleId == null) return;
    const status = String(r.status_code ?? r.status ?? '').toLowerCase();
    if (status === 'cancelled' || status === 'nonreserved') return;
    const rStart = r.start_date ? new Date(String(r.start_date).replace(' ', 'T')).getTime() : null;
    const rEnd = r.end_date ? new Date(String(r.end_date).replace(' ', 'T')).getTime() : null;
    if (rStart == null || rEnd == null) return;
    if (rStart <= periodEnd && rEnd >= periodStart) reservedVehicleIds.add(vehicleId);
  });

  const typeVehicles = vehicles.filter((v: any) => {
    if (v.out_of_use === true) return false;
    return Number(v.vehicle_type_id) === Number(vehicleTypeId);
  });

  const available = typeVehicles.find(
    (v: any) => !reservedVehicleIds.has(v.id ?? v.vehicle_id),
  );

  if (!available) return null;
  return {
    id: Number((available as any).id ?? (available as any).vehicle_id),
    vehicle_code: String((available as any).vehicle_code ?? ''),
    vehicle_number: String((available as any).vehicle_number ?? ''),
  };
}

export async function runSearchVehicleTypes(args: Record<string, any>): Promise<any> {
  const query = String(args.query || '').trim();
  if (!query) throw new Error('query is required');

  const pickupDate = args.pickup_date ? String(args.pickup_date).slice(0, 10) : null;
  const returnDate = args.return_date ? String(args.return_date).slice(0, 10) : null;
  const withCalc = !!(pickupDate && returnDate);

  // 車両タイプマスタ + bookingCalc（期間指定時のみ）を並列取得
  const [{ codeToId, idToCode, byId }, calcResult] = await Promise.all([
    getVehicleTypeCodeMaps(),
    withCalc
      ? fetchBookingCalc({
          start: toCalcDateTimeFromAny(pickupDate!),
          end: toCalcDateTimeFromAny(returnDate!),
        }).catch(() => null)
      : Promise.resolve<BookingCalcResponse | null>(null),
  ]);

  // bookingCalc の結果を code でインデックス
  const calcByCode = new Map<string, BookingCalcVehicle>();
  if (calcResult) {
    calcResult.vehicles.forEach((v) => calcByCode.set(v.code, v));
  }

  const q = query.toLowerCase();

  // タイプ名 / コード / カテゴリで部分一致
  const matchedIds = new Set<number>();
  const matchReasons = new Map<number, string>();
  byId.forEach((t, id) => {
    const name = String((t as any)?.name ?? (t as any)?.vehicle_type_name ?? '').toLowerCase();
    const code = String((t as any)?.code ?? (t as any)?.vehicle_type_code ?? '').toLowerCase();
    const category = String((t as any)?.category ?? '').toLowerCase();
    if (name.includes(q) || code.includes(q) || category.includes(q)) {
      matchedIds.add(id);
      if (name.includes(q)) matchReasons.set(id, 'type_name');
      else if (code.includes(q)) matchReasons.set(id, 'type_code');
      else matchReasons.set(id, 'type_category');
    }
  });

  // 0 件のときだけ vehicle_code / vehicle_number で具体車両も検索
  if (matchedIds.size === 0) {
    try {
      const vehicleLookup = await fetchDispatchVehicles({});
      vehicleLookup.forEach((v: any) => {
        const hay = [v.vehicle_code, v.vehicle_number, v.name]
          .filter(Boolean)
          .map((s: any) => String(s).toLowerCase())
          .join(' ');
        if (hay.includes(q)) {
          const tid = Number(v.vehicle_type_id);
          if (Number.isFinite(tid) && tid > 0 && !matchedIds.has(tid)) {
            matchedIds.add(tid);
            const reason = String(v.vehicle_code || '').toLowerCase().includes(q)
              ? 'vehicle_code'
              : String(v.vehicle_number || '').toLowerCase().includes(q)
                ? 'vehicle_number'
                : 'vehicle_name';
            matchReasons.set(tid, reason);
          }
        }
      });
    } catch { /* ignore */ }
  }

  const formatType = (id: number) => {
    const t = byId.get(id);
    const code = idToCode.get(id) || String((t as any)?.code ?? '');
    const calcVehicle = calcByCode.get(code);
    const info: any = {
      vehicle_type_id: id,
      code,
      name: (t as any)?.name ?? calcVehicle?.name_ja,
      category: (t as any)?.category,
      capacity: (t as any)?.capacity,
      base_price: calcVehicle?.base_price ?? (t as any)?.base_price ?? null,
    };
    const reason = matchReasons.get(id);
    if (reason) info.match_reason = reason;
    if (calcVehicle) {
      info.total_price = calcVehicle.breakdown.grand_total;
      info.availability = {
        period: { pickup_date: pickupDate, return_date: returnDate },
        total_vehicles: calcVehicle.total_stock,
        available_vehicles: calcVehicle.available_stock,
        reserved_vehicles: calcVehicle.total_stock - calcVehicle.available_stock,
        has_stock: calcVehicle.available_stock > 0,
      };
    }
    return info;
  };

  if (matchedIds.size === 0) {
    const allIds = Array.from(byId.keys()).slice(0, 20);
    return {
      count: 0,
      results: [],
      alternatives: allIds.map(formatType),
      note: 'クエリに合致する車両タイプ・車両コードが見つかりませんでした。alternatives から候補を選んでください。',
      query: { query, pickup_date: pickupDate, return_date: returnDate },
    };
  }

  return {
    count: matchedIds.size,
    results: Array.from(matchedIds).slice(0, 10).map(formatType),
    query: { query, pickup_date: pickupDate, return_date: returnDate },
  };
}

export async function runListAllVehicleTypes(args: Record<string, any>): Promise<any> {
  if (!args.pickup_date || !args.return_date) {
    throw new Error('pickup_date と return_date は必須です');
  }
  const pickupYmd = String(args.pickup_date).slice(0, 10);
  const returnYmd = String(args.return_date).slice(0, 10);
  const start = toCalcDateTimeFromAny(pickupYmd);
  const end = toCalcDateTimeFromAny(returnYmd);

  const [calcResult, { codeToId }] = await Promise.all([
    fetchBookingCalc({ start, end }),
    getVehicleTypeCodeMaps(),
  ]);

  const results = calcResult.vehicles.map((v) => ({
    vehicle_type_id: codeToId.get(v.code) ?? null,
    code: v.code,
    name: v.name_ja || v.name,
    base_price: v.base_price,
    first_night_price: v.first_night_price,
    total_price: v.breakdown.grand_total,
    has_stock: v.available_stock > 0,
    available_vehicles: v.available_stock,
    total_vehicles: v.total_stock,
    is_request_reservation: v.is_request_reservation,
    campaign_eligible: v.campaign_eligible,
    etc_card_free: v.etc_card_free,
    breakdown: v.breakdown,
  }));

  // 料金の安いものから並べる（ユーザーが選びやすい）
  results.sort((a, b) => a.total_price - b.total_price);

  return {
    period: {
      pickup_date: pickupYmd,
      return_date: returnYmd,
      assumed_pickup_time: '12:00',
      assumed_return_time: '12:00',
    },
    nights: calcResult.duration.nights,
    days: calcResult.duration.option_days,
    short: calcResult.duration.short,
    campaign: calcResult.campaign,
    count: results.length,
    vehicle_types: results,
  };
}

export async function runCalculatePrice(args: Record<string, any>): Promise<any> {
  const vehicleTypeId = Number(args.vehicle_type_id);
  if (!Number.isFinite(vehicleTypeId) || vehicleTypeId <= 0) {
    throw new Error('vehicle_type_id は正の整数で指定してください');
  }

  // pickup/return: datetime を優先、無ければ date を 12:00 仮定で使う
  const pickupSrc = args.pickup_datetime || args.pickup_date;
  const returnSrc = args.return_datetime || args.return_date;
  if (!pickupSrc || !returnSrc) {
    throw new Error('pickup_datetime/return_datetime もしくは pickup_date/return_date を指定してください');
  }
  const start = toCalcDateTimeFromAny(String(pickupSrc));
  const end = toCalcDateTimeFromAny(String(returnSrc));

  const wantsNoc = args.noc_waiver === true;
  const wantsCdw = args.cdw === true;
  const childSeat = Math.max(0, Number(args.child_seat) || 0);
  const boosterSeat = Math.max(0, Number(args.booster_seat) || 0);
  const quantity = Math.max(1, Math.floor(Number(args.quantity) || 1));

  // 1 回目: 要求された組合せで計算
  const [{ idToCode, byId }, calcMain] = await Promise.all([
    getVehicleTypeCodeMaps(),
    fetchBookingCalc({
      start, end,
      noc: wantsNoc, cdw: wantsCdw,
      child_seat: childSeat, booster_seat: boosterSeat,
    }),
  ]);

  const code = idToCode.get(vehicleTypeId);
  if (!code) {
    throw new Error(`vehicle_type_id=${vehicleTypeId} に対応する車両タイプコードが見つかりません`);
  }
  const target = calcMain.vehicles.find((v) => v.code === code);
  if (!target) {
    throw new Error(`vehicle_type_id=${vehicleTypeId} (code=${code}) は料金計算結果に含まれていません`);
  }

  const breakdown = target.breakdown;
  const totalInsurance = breakdown.insurance;

  // NOC + CDW 両方選択時は NOC 単独でもう 1 回計算して、insurance の差分から内訳を取得
  let nocPrice = 0;
  let cdwPrice = 0;
  if (wantsNoc && wantsCdw) {
    try {
      const calcNocOnly = await fetchBookingCalc({
        start, end,
        noc: true, cdw: false,
        child_seat: childSeat, booster_seat: boosterSeat,
      });
      const nocVehicle = calcNocOnly.vehicles.find((v) => v.code === code);
      nocPrice = Number(nocVehicle?.breakdown.insurance) || 0;
      cdwPrice = Math.max(0, totalInsurance - nocPrice);
    } catch {
      // フォールバック: 取得失敗時は noc 側にまとめる
      nocPrice = totalInsurance;
      cdwPrice = 0;
    }
  } else if (wantsNoc) {
    nocPrice = totalInsurance;
  } else if (wantsCdw) {
    cdwPrice = totalInsurance;
  }

  const typeMeta = byId.get(vehicleTypeId);
  const perVehicleTotal = breakdown.grand_total;

  return {
    vehicle_type_id: vehicleTypeId,
    code,
    vehicle_type_name: target.name_ja || (typeMeta as any)?.name || code,
    period: {
      pickup_datetime: start,
      return_datetime: end,
      nights: calcMain.duration.nights,
      days: calcMain.duration.option_days,
      short: calcMain.duration.short,
    },
    options_requested: {
      noc_waiver: wantsNoc,
      cdw: wantsCdw,
      child_seat: childSeat,
      booster_seat: boosterSeat,
    },
    per_vehicle: {
      base_subtotal: breakdown.subtotal_base,        // キャンペーン適用前の基本料金
      campaign_discount: breakdown.discount,         // キャンペーン割引額
      after_campaign: breakdown.subtotal_base - breakdown.discount, // ＝割引後（オプション抜き）
      noc_price: nocPrice,
      cdw_price: cdwPrice,
      options_price: breakdown.options,              // チャイルド/ブースター等
      fees: breakdown.fees,
      grand_total: perVehicleTotal,                  // すべての加算後（1 台分）
    },
    quantity,
    grand_total: perVehicleTotal * quantity,         // quantity 台分の合計
    campaign: calcMain.campaign,
    has_stock: target.available_stock >= quantity,
    available_vehicles: target.available_stock,
    total_vehicles: target.total_stock,
    // 後方互換: 旧 calculate_price 利用者のために残す（同じ値）
    base_price: breakdown.subtotal_base,
    discount: breakdown.discount,
    total_price: perVehicleTotal,
    nights: calcMain.duration.nights,
    days: calcMain.duration.option_days,
    short: calcMain.duration.short,
    breakdown,
  };
}

export async function runSearchCustomer(args: Record<string, any>): Promise<any> {
  if (!args.name && !args.phone) {
    throw new Error('name または phone を指定してください');
  }
  const today = new Date();
  const startDate = toYmd(addDays(today, -365));
  const endDate = toYmd(addDays(today, 30));

  const reservations = await fetchDispatchReservations({ startDate, endDate });
  const name = args.name ? String(args.name).toLowerCase() : null;
  const phone = args.phone ? normalizePhone(String(args.phone)) : null;

  const byKey = new Map<string, { customer_name: string; customer_tel: string | null; customer_email: string | null; reservation_count: number; latest: string | null }>();

  reservations.forEach((r: any) => {
    const cname = r.customer_name ?? r.cname ?? '';
    const ctel = r.customer_tel ?? r.tel ?? null;
    const cmail = r.customer_email ?? r.mail ?? null;

    if (name && !String(cname).toLowerCase().includes(name)) return;
    if (phone && (!ctel || !normalizePhone(String(ctel)).includes(phone))) return;

    const key = `${cname}|${ctel ?? ''}`;
    const prev = byKey.get(key);
    const startIso = r.start_date || '';
    if (prev) {
      prev.reservation_count += 1;
      if (startIso > (prev.latest || '')) prev.latest = startIso;
    } else {
      byKey.set(key, {
        customer_name: cname,
        customer_tel: ctel,
        customer_email: cmail,
        reservation_count: 1,
        latest: startIso,
      });
    }
  });

  const list = Array.from(byKey.values()).sort((a, b) => (b.latest || '').localeCompare(a.latest || ''));
  return {
    count: list.length,
    results: list.slice(0, 20),
  };
}

export async function runCreateReservation(
  args: Record<string, any>,
  context: {
    requestConfirm: (args: Record<string, any>) => Promise<boolean>;
    requestWizard?: (fields: WizardField[]) => Promise<Record<string, any>>;
  },
): Promise<any> {
  // 超必須項目チェック（customer_name はフロントウィザードで収集するためここでは要求しない）
  const required = ['pickup_datetime', 'return_datetime', 'vehicle_type_id'];
  const missing = required.filter((k) => args[k] === undefined || args[k] === null || args[k] === '');
  if (missing.length > 0) {
    throw new Error(`必須項目が不足しています: ${missing.join(', ')}`);
  }

  // 必須・準必須含む全項目のうち未指定のものはフロントのウィザードで一問一答収集
  // （Gemini は重要質問を自分で聞かない方針。systemPrompt で制御）
  if (context.requestWizard) {
    const pending = RESERVATION_WIZARD_FIELDS.filter(
      (f) => args[f.key] === undefined || args[f.key] === null || args[f.key] === '',
    );
    if (pending.length > 0) {
      const collected = await context.requestWizard(pending);
      Object.keys(collected).forEach((k) => {
        if (collected[k] !== undefined && collected[k] !== null && collected[k] !== '') {
          args[k] = collected[k];
        }
      });
    }
  }

  // 必須項目（予約者名）がウィザード後にも欠けている場合は中断
  if (!args.customer_name || String(args.customer_name).trim() === '') {
    return { success: false, error: '予約者名が指定されなかったため予約を作成できませんでした' };
  }

  // 自社予約サイト calc API で料金計算（NOC/CDW/オプション/キャンペーン込み）
  const startCal = toCalcDateTime(args.pickup_datetime);
  const endCal = toCalcDateTime(args.return_datetime);
  const childSeat = Number(args.child_seat) || 0;
  const boosterSeat = Number(args.booster_seat) || 0;
  const wantsNoc = args.noc_waiver === true;
  const wantsCdw = args.cdw === true;

  const { idToCode, byId } = await getVehicleTypeCodeMaps();
  const code = idToCode.get(Number(args.vehicle_type_id));
  if (!code) {
    return {
      success: false,
      error: `vehicle_type_id=${args.vehicle_type_id} に対応する車両タイプコードが見つかりません`,
    };
  }
  const typeName = (byId.get(Number(args.vehicle_type_id)) as any)?.name || code;

  const calcMain = await fetchBookingCalc({
    start: startCal,
    end: endCal,
    noc: wantsNoc,
    cdw: wantsCdw,
    child_seat: childSeat,
    booster_seat: boosterSeat,
  });
  const targetVehicle = calcMain.vehicles.find((v) => v.code === code);
  if (!targetVehicle) {
    return {
      success: false,
      error: `${typeName} (${code}) は料金計算結果に含まれていません`,
    };
  }
  const breakdown = targetVehicle.breakdown;
  const optionDays = calcMain.duration.option_days;
  const totalInsurance = breakdown.insurance;

  // NOC / CDW を別々に保存するため、両方選択時は NOC 単体で再計算して内訳を取得
  let nocPrice = 0;
  let cdwPrice = 0;
  if (wantsNoc && wantsCdw) {
    try {
      const calcNocOnly = await fetchBookingCalc({
        start: startCal,
        end: endCal,
        noc: true,
        cdw: false,
        child_seat: childSeat,
        booster_seat: boosterSeat,
      });
      const nocVehicle = calcNocOnly.vehicles.find((v) => v.code === code);
      nocPrice = Number(nocVehicle?.breakdown.insurance) || 0;
      cdwPrice = Math.max(0, totalInsurance - nocPrice);
    } catch {
      // フォールバック: 取得失敗時は noc 側にまとめる
      nocPrice = totalInsurance;
      cdwPrice = 0;
    }
  } else if (wantsNoc) {
    nocPrice = totalInsurance;
  } else if (wantsCdw) {
    cdwPrice = totalInsurance;
  }

  // ユーザー確認（手数料内訳を渡して確認カードに表示）
  const confirmed = await context.requestConfirm({
    ...args,
    base_price: breakdown.subtotal_base,
    discount: breakdown.discount,
    coupon_applied: breakdown.coupon_applied,
    options_price: breakdown.options,
    fees_price: breakdown.fees,
    noc_price: nocPrice,
    cdw_price: cdwPrice,
    insurance: totalInsurance,
    sum_price: breakdown.grand_total,
    days: optionDays,
    nights: calcMain.duration.nights,
    short: calcMain.duration.short,
    vehicle_type_name: typeName,
    vehicle_type_code: code,
    campaign: calcMain.campaign,
    breakdown,
  });
  if (!confirmed) {
    return { success: false, cancelled: true, message: 'ユーザーが予約作成をキャンセルしました' };
  }

  const startIso = ensureDateTimeIso(args.pickup_datetime);
  const endIso = ensureDateTimeIso(args.return_datetime);

  // 具体車両を 1 台選ぶ（vehicle_id を送らないとバックエンドが任意に自動割当してしまうため）
  const pickupYmd = String(args.pickup_datetime).slice(0, 10);
  const returnYmd = String(args.return_datetime).slice(0, 10);
  const picked = await pickAvailableVehicleForType(Number(args.vehicle_type_id), pickupYmd, returnYmd);
  if (!picked) {
    return {
      success: false,
      error: `指定期間で ${typeName || `車両タイプID ${args.vehicle_type_id}`} の空き車両が見つかりませんでした`,
    };
  }

  const comment = String(args.comment || '').trim();
  const sanitizedEmail = sanitizeEmail(args.email);
  const sanitizedPhone = sanitizePhone(args.phone);

  // キャンペーン名は detail.coupon に短く保存
  const couponName =
    breakdown.coupon_applied && calcMain.campaign?.name
      ? String(calcMain.campaign.name).slice(0, 255)
      : '';

  // 予約データの memo（タイムライン形式）にエージェント実行ログを記録
  const memoEntry = buildAgentMemoEntry('reservation', [
    'エージェント経由で新規予約を作成',
    `車両: ${typeName} (${code})`,
    `期間: ${formatDateTime(startIso)} 〜 ${formatDateTime(endIso)}`,
    `合計: ¥${Number(breakdown.grand_total).toLocaleString()}`,
  ]);

  const detail: Record<string, any> = {
    base_price: breakdown.subtotal_base,
    noc: wantsNoc,
    noc_price: nocPrice,
    cdw: wantsCdw,
    cdw_price: cdwPrice,
    pax: Number(args.pax) || 0,
    pax_children: Number(args.pax_children) || 0,
    child_seat: childSeat,
    booster_seat: boosterSeat,
    arrival_flight: String(args.arrival_flight || '').trim(),
    departure_flight: String(args.departure_flight || '').trim(),
    option_price: breakdown.options,
    sum_price: breakdown.grand_total,
    days: optionDays,
    nights: calcMain.duration.nights,
    coupon: couponName,
    point: -Number(breakdown.discount || 0),
    comment: comment || '',
  };

  const reservationInput: any = {
    status: 'booked',
    memo: memoEntry, // root に置けば buildDetailPayload が detail.memo に格納する
    start_date: startIso,
    end_date: endIso,
    vehicle_id: picked.id,
    customer_name: String(args.customer_name),
    customer_tel: sanitizedPhone,
    customer_email: sanitizedEmail,
    car_type: typeName,
    detail,
    json_data: {
      agent_created: true,
      breakdown,
      campaign: calcMain.campaign,
      duration: calcMain.duration,
    },
  };

  // status: 'booked' の文字列→ID変換が statusMapper キャッシュに依存するため、
  // 保存直前にキャッシュが populate されていることを保証する
  await ensureStatusCachePopulated();

  const saved = await saveDispatchReservation(reservationInput);
  return {
    success: true,
    reservation_id: saved?.id ?? null,
    base_price: breakdown.subtotal_base,
    noc_price: nocPrice,
    cdw_price: cdwPrice,
    discount: breakdown.discount,
    sum_price: breakdown.grand_total,
    days: optionDays,
    nights: calcMain.duration.nights,
    vehicle_type_name: typeName,
    campaign: calcMain.campaign,
    assigned_vehicle: {
      id: picked.id,
      vehicle_code: picked.vehicle_code,
      vehicle_number: picked.vehicle_number,
    },
  };
}

// -------- update_reservation --------

/** どのキーが「実際に上書き対象として渡された」かを判定するためのホワイトリスト */
const UPDATE_FIELD_KEYS = [
  'status',
  'pickup_datetime',
  'return_datetime',
  'arrival_flight',
  'departure_flight',
  'customer_name',
  'phone',
  'email',
  'pax',
  'pax_children',
  'child_seat',
  'booster_seat',
  'noc_waiver',
  'cdw',
  'comment',
  'memo',
] as const;

/** 表示用ラベル */
const UPDATE_FIELD_LABELS: Record<string, string> = {
  status: 'ステータス',
  pickup_datetime: '貸出日時',
  return_datetime: '返却日時',
  arrival_flight: '到着便',
  departure_flight: '出発便',
  customer_name: '予約者名',
  phone: '電話番号',
  email: 'メール',
  pax: '乗車人数（大人）',
  pax_children: '子供の人数',
  child_seat: 'チャイルドシート',
  booster_seat: 'ブースターシート',
  noc_waiver: 'NOC免責',
  cdw: 'CDW',
  comment: 'お客様の要望',
  memo: '社内メモ',
};

const STATUS_LABEL_MAP: Record<string, string> = {
  booked: '予約確定',
  done: '貸出済み',
  in_use: '貸出中',
  pending: '確認中',
  received: '受信済み',
  mail_ready: '要連絡',
  offer: 'オファー中',
  request: 'リクエスト',
  cancelled: 'キャンセル',
  nonreserved: '予約に至らなかった',
  full: '予約がいっぱい',
};

const formatStatus = (code: any): string => {
  if (!code) return '';
  const key = String(code).toLowerCase();
  return STATUS_LABEL_MAP[key] ? `${STATUS_LABEL_MAP[key]} (${key})` : String(code);
};

const formatDateTime = (v: any): string => {
  if (!v) return '';
  const s = String(v).replace('T', ' ').slice(0, 16);
  return s;
};

const formatBool = (v: any): string => {
  if (v === true || v === 1 || v === '1' || v === 'true') return 'あり';
  if (v === false || v === 0 || v === '0' || v === 'false') return 'なし';
  return '';
};

const formatFieldValue = (key: string, value: any): string => {
  if (value === null || value === undefined || value === '') return '（未設定）';
  switch (key) {
    case 'status':
      return formatStatus(value);
    case 'pickup_datetime':
    case 'return_datetime':
      return formatDateTime(value);
    case 'noc_waiver':
    case 'cdw':
      return formatBool(value);
    default:
      return String(value);
  }
};

export async function runUpdateReservation(
  args: Record<string, any>,
  context: {
    requestUpdateConfirm?: (
      before: Record<string, any>,
      changes: Array<{ key: string; label: string; before: string; after: string }>,
    ) => Promise<boolean>;
  },
): Promise<any> {
  const reservationId = parseInt(String(args.reservation_id || '').trim(), 10);
  const contactCode = typeof args.contact_code === 'string' ? args.contact_code.trim() : '';
  if (!Number.isFinite(reservationId) && !contactCode) {
    return { success: false, error: 'reservation_id または contact_code を指定してください' };
  }

  // 既存予約を取得
  const fetchParams: Record<string, any> = {};
  if (Number.isFinite(reservationId) && reservationId > 0) {
    // dispatch API は id 直接フィルタが無いため広い日付で取得後にフィルタ
    const today = new Date();
    fetchParams.startDate = toYmd(addDays(today, -365));
    fetchParams.endDate = toYmd(addDays(today, 365));
  } else {
    fetchParams.contactCode = contactCode;
  }

  const reservations = await fetchDispatchReservations(fetchParams);
  const target = (reservations as any[]).find((r) => {
    if (Number.isFinite(reservationId) && reservationId > 0) {
      return Number(r.id ?? r.reservation_id) === reservationId;
    }
    return String(r.contact_code || '') === contactCode;
  });

  if (!target) {
    const idDesc = Number.isFinite(reservationId) ? `id=${reservationId}` : `contact_code=${contactCode}`;
    return { success: false, error: `対象の予約が見つかりませんでした (${idDesc})` };
  }

  const detail: any = (target as any).detail || {};

  // 既存値の正規化（差分比較用）
  const existingValues: Record<string, any> = {
    status: (target as any).status_code ?? (target as any).status,
    pickup_datetime: (target as any).start_date,
    return_datetime: (target as any).end_date,
    arrival_flight: detail.arrival_flight,
    departure_flight: detail.departure_flight,
    customer_name: (target as any).customer_name,
    phone: (target as any).customer_tel,
    email: (target as any).customer_email,
    pax: detail.pax,
    pax_children: detail.pax_children,
    child_seat: detail.child_seat,
    booster_seat: detail.booster_seat,
    noc_waiver: detail.noc,
    cdw: detail.cdw,
    comment: detail.comment,
    memo: detail.memo,
  };

  // 渡された項目だけを「変更対象」として抽出。値が既存と同じものは除外
  const changes: Array<{ key: string; label: string; before: string; after: string; rawAfter: any }> = [];
  for (const key of UPDATE_FIELD_KEYS) {
    if (!(key in args)) continue;
    const newValue = (args as any)[key];
    const oldValue = existingValues[key];
    const beforeStr = formatFieldValue(key, oldValue);
    const afterStr = formatFieldValue(key, newValue);
    if (beforeStr === afterStr) continue; // no-op
    changes.push({ key, label: UPDATE_FIELD_LABELS[key] || key, before: beforeStr, after: afterStr, rawAfter: newValue });
  }

  if (changes.length === 0) {
    return { success: false, error: '変更項目が指定されていない、または既存値と同じです' };
  }

  // 確認カードを表示
  const beforeSnapshot = {
    reservation_id: (target as any).id,
    contact_code: (target as any).contact_code,
    customer_name: (target as any).customer_name,
    car_type: (target as any).car_type,
    pickup_datetime: formatDateTime((target as any).start_date),
    return_datetime: formatDateTime((target as any).end_date),
    status: formatStatus((target as any).status_code ?? (target as any).status),
  };

  const confirmed = context.requestUpdateConfirm
    ? await context.requestUpdateConfirm(beforeSnapshot, changes.map(({ key, label, before, after }) => ({ key, label, before, after })))
    : true;

  if (!confirmed) {
    return { success: false, cancelled: true, message: 'ユーザーが更新をキャンセルしました' };
  }

  // 既存予約をベースに、変更項目だけ上書き。
  //
  // ★重要★ mapReservation は detail フィールドを **ルートにも spread** するため、
  // 取得した予約は target.detail.child_seat と target.child_seat の両方に値を持つ。
  // buildDetailPayload の pick = input[key] ?? input.detail[key] は **ルート優先** なので、
  // detail 側だけ書き換えても root に古い値が残っていれば古い値が保存される。
  // → detail 系フィールドはルートと detail の両方に上書きする必要がある。
  const merged: any = { ...(target as any) };
  merged.detail = { ...detail };

  let userMemoOverride: string | null = null; // ユーザー明示の memo 上書き（あれば後で監査ログを追記する起点に使う）

  // detail 内・ルート両方に書く必要があるキー
  const setDetail = (k: string, v: any) => {
    merged.detail[k] = v;
    merged[k] = v;
  };

  for (const { key, rawAfter } of changes) {
    switch (key) {
      case 'status':
        merged.status = rawAfter; // 'cancelled' などの文字列。resolveStatusId が ID に変換
        break;
      case 'pickup_datetime':
        merged.start_date = ensureDateTimeIso(String(rawAfter));
        break;
      case 'return_datetime':
        merged.end_date = ensureDateTimeIso(String(rawAfter));
        break;
      case 'phone':
        merged.customer_tel = sanitizePhone(rawAfter);
        break;
      case 'email':
        merged.customer_email = sanitizeEmail(rawAfter);
        break;
      case 'customer_name':
        merged.customer_name = String(rawAfter);
        break;
      case 'noc_waiver':
        setDetail('noc', rawAfter === true);
        break;
      case 'cdw':
        setDetail('cdw', rawAfter === true);
        break;
      case 'memo':
        userMemoOverride = String(rawAfter ?? '');
        break;
      case 'arrival_flight':
      case 'departure_flight':
      case 'pax':
      case 'pax_children':
      case 'child_seat':
      case 'booster_seat':
      case 'comment':
        setDetail(key, rawAfter);
        break;
      default:
        break;
    }
  }

  // 監査ログを memo タイムラインに追記
  // 1) ユーザー明示の memo 変更があればそれを「現状」にしてその後ろへエージェント実行ログを足す
  // 2) なければ既存 memo へ追記
  const isCancel = changes.some(
    (c) => c.key === 'status' && /cancelled/i.test(String(c.rawAfter)),
  );
  const auditLines = [
    isCancel ? 'エージェント経由で予約をキャンセル' : 'エージェント経由で予約を更新',
    ...changes
      .filter((c) => c.key !== 'memo')
      .map((c) => `${c.label}: ${c.before || '（未設定）'} → ${c.after}`),
  ];
  const auditEntry = buildAgentMemoEntry(isCancel ? 'cancel' : 'reservation', auditLines);
  const baseMemo = userMemoOverride !== null ? userMemoOverride : (target as any).memo;
  merged.memo = appendAgentMemoEntry(baseMemo, auditEntry);

  // status 文字列→ID 変換のために statusMapper キャッシュが populate されていることを保証
  await ensureStatusCachePopulated();

  const saved = await saveDispatchReservation(merged);
  return {
    success: true,
    reservation_id: saved?.id ?? (target as any).id,
    contact_code: (target as any).contact_code,
    changes: changes.map(({ key, label, before, after }) => ({ key, label, before, after })),
  };
}

// -------- Dispatcher --------

export async function dispatchTool(
  name: string,
  args: Record<string, any>,
  context: {
    requestConfirm: (args: Record<string, any>) => Promise<boolean>;
    requestUpdateConfirm?: (
      before: Record<string, any>,
      changes: Array<{ key: string; label: string; before: string; after: string }>,
    ) => Promise<boolean>;
    requestWizard?: (fields: WizardField[]) => Promise<Record<string, any>>;
  },
): Promise<any> {
  switch (name) {
    case 'search_reservations':
      return runSearchReservations(args);
    case 'search_vehicle_types':
      return runSearchVehicleTypes(args);
    case 'list_all_vehicle_types':
      return runListAllVehicleTypes(args);
    case 'calculate_price':
      return runCalculatePrice(args);
    case 'search_customer':
      return runSearchCustomer(args);
    case 'create_reservation':
      return runCreateReservation(args, context);
    case 'update_reservation':
      return runUpdateReservation(args, context);
    default:
      throw new Error(`unknown tool: ${name}`);
  }
}

export const AGENT_STEP_LABELS: Record<string, { running: string; success: (result: any) => string }> = {
  search_reservations: {
    running: '🔍 予約を検索中…',
    success: (r) => `✅ 予約 ${r?.count ?? 0} 件見つかりました${r?.truncated ? '（30件まで表示）' : ''}`,
  },
  search_vehicle_types: {
    running: '🚗 車両タイプを検索中…',
    success: (r) => {
      if ((r?.count ?? 0) === 0) {
        return `⚠️ 該当なし（候補 ${r?.alternatives?.length ?? 0} 件を提示）`;
      }
      const withStock = Array.isArray(r?.results)
        ? r.results.filter((x: any) => x.availability?.has_stock).length
        : 0;
      const hasStockInfo = Array.isArray(r?.results) && r.results.some((x: any) => x.availability);
      return hasStockInfo
        ? `✅ 車両タイプ ${r.count} 件（在庫あり ${withStock} 件）`
        : `✅ 車両タイプ ${r.count} 件見つかりました`;
    },
  },
  list_all_vehicle_types: {
    running: '🚙 全車両タイプの料金を取得中…',
    success: (r) => {
      const n = r?.vehicle_types?.length ?? 0;
      const withStock = Array.isArray(r?.vehicle_types)
        ? r.vehicle_types.filter((x: any) => x.has_stock).length
        : 0;
      const campaign = r?.campaign?.name ? ` ／🎉${r.campaign.name}` : '';
      return `✅ 車両タイプ ${n} 件（在庫あり ${withStock} 件）${campaign}`;
    },
  },
  calculate_price: {
    running: '💴 料金を計算中…',
    success: (r) => {
      const qty = Number(r?.quantity || 1);
      const perVehicle = Number(r?.per_vehicle?.grand_total ?? r?.total_price ?? 0).toLocaleString();
      const grandTotal = Number(r?.grand_total ?? r?.total_price ?? 0).toLocaleString();
      const dis = Number(r?.per_vehicle?.campaign_discount ?? r?.discount ?? 0);
      const disText = dis > 0 ? ` （割引 -${dis.toLocaleString()}円）` : '';
      const nights = r?.period?.nights ?? r?.nights ?? r?.days ?? 0;
      if (qty > 1) return `✅ ${nights} 泊 ${qty} 台 合計 ${grandTotal}円${disText}（1 台 ${perVehicle}円）`;
      return `✅ ${nights} 泊 合計 ${grandTotal}円${disText}`;
    },
  },
  search_customer: {
    running: '👤 顧客を検索中…',
    success: (r) => `✅ 顧客 ${r?.count ?? 0} 件見つかりました`,
  },
  create_reservation: {
    running: '📝 予約を作成中…',
    success: (r) => {
      if (r?.cancelled) return '⚠️ 予約作成はキャンセルされました';
      if (r?.success === false) return `⚠️ ${r?.error ?? '予約作成に失敗しました'}`;
      const vehicle = r?.assigned_vehicle?.vehicle_code ? ` / 車両 ${r.assigned_vehicle.vehicle_code}` : '';
      return `✅ 予約を作成しました (ID: ${r?.reservation_id ?? '-'}${vehicle})`;
    },
  },
  update_reservation: {
    running: '✏️ 予約を更新中…',
    success: (r) => {
      if (r?.cancelled) return '⚠️ 更新はキャンセルされました';
      if (r?.success === false) return `⚠️ ${r?.error ?? '予約更新に失敗しました'}`;
      const code = r?.contact_code ? ` (${r.contact_code})` : '';
      const n = Array.isArray(r?.changes) ? r.changes.length : 0;
      return `✅ 予約を更新しました${code} ／ ${n} 項目`;
    },
  },
};
